import React, { useState, useEffect } from 'react';
import { ShoppingCart, Search, Plus, Minus, CreditCard, Banknote, User, Package, Clock, LogOut, MapPin, Settings, X, ChevronUp, Edit } from 'lucide-react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import PaymentModal from '../../components/PaymentModal';
import OrderCompleteModal from './OrderCompleteModal';

// Helper to get auth headers including tenant site ID
const getHeaders = () => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
  const activeSiteId = localStorage.getItem('activeSiteId');
  if (activeSiteId) {
    headers['x-site-id'] = activeSiteId;
  }
  return headers;
};

// Temporary inline API service until import path is fixed
const servicesAPI = {
  getServices: async () => {
    const response = await fetch('/api/pos/services', {
      headers: getHeaders(),
    });
    return response.json();
  },
};

interface Service {
  _id: string;
  name: string;
  category: string;
  basePrice: number;
  pricingType: 'per_item' | 'per_kg' | 'fixed';
  isExpress: boolean;
  expressSurcharge: number;
}

interface OrderItem {
  serviceId: string;
  service: Service;
  quantity: number;
  weight?: number;
  specialInstructions?: string;
  isExpress: boolean;
  totalPrice: number;
}

interface Customer {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  postcode?: string;
  city?: string;
  gdprConsent?: boolean;
  marketingConsent?: boolean;
  preferredNotificationMethod?: 'email' | 'sms' | 'both';
}

const POS: React.FC = () => {
  const { user, logout, activeSiteName } = useAuth();
  const [services, setServices] = useState<Service[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [serviceSearch, setServiceSearch] = useState('');
  const [cart, setCart] = useState<OrderItem[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showCartDrawer, setShowCartDrawer] = useState(false);
  const [customerFormErrors, setCustomerFormErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    postcode?: string;
    city?: string;
    address?: string;
    form?: string;
  }>({});
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    postcode: '',
    city: '',
    address: '',
    gdprConsent: false,
    marketingConsent: false,
    preferredNotificationMethod: 'email',
  });
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [completedOrderId, setCompletedOrderId] = useState<string | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [dueDate, setDueDate] = useState<string>(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });

  const { data: servicesData, error: servicesError } = useQuery('services', async () => {
    const activeSiteId = localStorage.getItem('activeSiteId');
    if (!activeSiteId) {
      console.log('No active site, skipping services fetch');
      return [];
    }
    // Add delay to prevent rapid calls
    await new Promise(resolve => setTimeout(resolve, 300));
    const data = await servicesAPI.getServices();
    return Array.isArray(data) ? data : data.services || [];
  }, {
    enabled: !!localStorage.getItem('activeSiteId'),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  const { data: customersData, error: customersError } = useQuery('customers', async () => {
    const activeSiteId = localStorage.getItem('activeSiteId');
    if (!activeSiteId) {
      console.log('No active site, skipping customers fetch');
      return [];
    }
    // Add delay to prevent rapid calls
    await new Promise(resolve => setTimeout(resolve, 300));
    const res = await fetch('/api/customers', {
      headers: getHeaders(),
    });
    const data = await res.json();
    return Array.isArray(data) ? data : data.customers || [];
  }, {
    enabled: !!localStorage.getItem('activeSiteId'),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  useEffect(() => {
    if (servicesData) setServices(servicesData);
  }, [servicesData]);

  useEffect(() => {
    if (customersData) setCustomers(customersData);
  }, [customersData]);

  useEffect(() => {
    if (servicesError) toast.error('Error loading services');
  }, [servicesError]);

  useEffect(() => {
    if (customersError) toast.error('Error loading customers');
  }, [customersError]);

  // Close drawer when a payment modal opens
  useEffect(() => {
    if (showPaymentModal) setShowCartDrawer(false);
  }, [showPaymentModal]);

  const categories = [
    { id: 'all', name: 'All', icon: Package },
    { id: 'washing', name: 'Washing', icon: Package },
    { id: 'dry_cleaning', name: 'Dry Cleaning', icon: Package },
    { id: 'ironing', name: 'Ironing', icon: Package },
    { id: 'alterations', name: 'Alterations', icon: Package },
    { id: 'special_care', name: 'Special Care', icon: Package },
  ];

  const filteredServices = services.filter((service) => {
    const matchesCategory = selectedCategory === 'all' || service.category === selectedCategory;
    const matchesSearch = service.name.toLowerCase().includes(serviceSearch.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const addToCart = (service: Service) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.serviceId === service._id);
      if (!existing) {
        return [
          ...prev,
          {
            serviceId: service._id,
            service,
            quantity: 1,
            isExpress: false,
            totalPrice: service.basePrice,
          },
        ];
      }
      return prev.map((i) => {
        if (i.serviceId !== service._id) return i;
        const quantity = i.quantity + 1;
        const base = service.pricingType === 'per_kg' && i.weight ? service.basePrice * i.weight : service.basePrice * quantity;
        const totalPrice = base + (i.isExpress && service.isExpress ? service.expressSurcharge : 0);
        return { ...i, quantity, totalPrice };
      });
    });
  };

  const removeFromCart = (serviceId: string) => {
    setCart((prev) => prev.filter((i) => i.serviceId !== serviceId));
  };

  const updateQuantity = (serviceId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(serviceId);
      return;
    }
    setCart((prev) =>
      prev.map((i) => {
        if (i.serviceId !== serviceId) return i;
        const base = i.service.pricingType === 'per_kg' && i.weight ? i.service.basePrice * i.weight : i.service.basePrice * quantity;
        const totalPrice = base + (i.isExpress && i.service.isExpress ? i.service.expressSurcharge : 0);
        return { ...i, quantity, totalPrice };
      })
    );
  };

  const updateWeight = (serviceId: string, weight: number) => {
    setCart((prev) =>
      prev.map((i) => {
        if (i.serviceId !== serviceId) return i;
        const base = i.service.basePrice * weight;
        const totalPrice = base + (i.isExpress && i.service.isExpress ? i.service.expressSurcharge : 0);
        return { ...i, weight, totalPrice };
      })
    );
  };

  const toggleExpress = (serviceId: string) => {
    setCart((prev) =>
      prev.map((i) => {
        if (i.serviceId !== serviceId) return i;
        const isExpress = !i.isExpress;
        const base = i.service.pricingType === 'per_kg' && i.weight ? i.service.basePrice * i.weight : i.service.basePrice * i.quantity;
        const totalPrice = base + (isExpress && i.service.isExpress ? i.service.expressSurcharge : 0);
        return { ...i, isExpress, totalPrice };
      })
    );
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, i) => sum + i.totalPrice, 0);
    const vat = subtotal * 0.2;
    const total = subtotal + vat;
    return { subtotal, vat, total };
  };

  const { subtotal, vat, total } = calculateTotals();

  const validateNewCustomer = () => {
    const errors: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      postcode?: string;
      city?: string;
      address?: string;
      form?: string;
    } = {};

    const firstName = newCustomer.firstName.trim();
    const lastName = newCustomer.lastName.trim();
    const email = newCustomer.email.trim();
    const phone = newCustomer.phone.trim();
    const postcode = newCustomer.postcode.trim();
    const city = newCustomer.city.trim();

    const normalizePhoneToE164 = (raw: string) => {
      const stripped = raw.replace(/[()\-\s]/g, '');
      if (!stripped) return '';
      if (stripped.startsWith('+')) return stripped;
      if (stripped.startsWith('00')) return `+${stripped.slice(2)}`;
      if (stripped.startsWith('0')) return `+44${stripped.slice(1)}`;
      return `+${stripped}`;
    };

    if (!firstName) errors.firstName = 'First name is required';
    if (!lastName) errors.lastName = 'Last name is required';
    if (!email) {
      errors.email = 'Email is required';
    } else {
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!isValidEmail) errors.email = 'Enter a valid email address';
    }

    if (!postcode) errors.postcode = 'Postcode is required';
    if (!city) errors.city = 'City is required';

    if (phone) {
      const e164 = normalizePhoneToE164(phone);
      const isValidE164 = /^\+?[1-9]\d{1,14}$/.test(e164);
      if (!isValidE164) {
        errors.phone = 'Phone must be in international format (e.g. +447886699820)';
      }
    }

    setCustomerFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveCustomer = async () => {
    setCustomerFormErrors({});
    if (!validateNewCustomer()) return;

    const normalizePhoneToE164 = (raw: string) => {
      const stripped = raw.replace(/[()\-\s]/g, '');
      if (!stripped) return '';
      if (stripped.startsWith('+')) return stripped;
      if (stripped.startsWith('00')) return `+${stripped.slice(2)}`;
      if (stripped.startsWith('0')) return `+44${stripped.slice(1)}`;
      return `+${stripped}`;
    };

    const payload = {
      ...newCustomer,
      phone: newCustomer.phone.trim() ? normalizePhoneToE164(newCustomer.phone.trim()) : '',
    };

    try {
      const url = isEditingCustomer && selectedCustomer ? `/api/customers/${selectedCustomer._id}` : '/api/customers';
      const method = isEditingCustomer ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        const serverErrors: {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          postcode?: string;
          city?: string;
          address?: string;
          form?: string;
        } = {};

        if (data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
          for (const [key, val] of Object.entries(data.errors)) {
            if (typeof val === 'string') (serverErrors as any)[key] = val;
            if (val && typeof val === 'object' && 'message' in (val as any)) {
              (serverErrors as any)[key] = (val as any).message;
            }
          }
        }

        if (Array.isArray(data?.errors)) {
          for (const err of data.errors) {
            if (!err || typeof err !== 'object') continue;
            const field = (err as any).param || (err as any).path || (err as any).field;
            const message = (err as any).msg || (err as any).message;
            if (field && typeof message === 'string') {
              (serverErrors as any)[field] = message;
            }
          }
        }

        if (Array.isArray(data?.details)) {
          for (const item of data.details) {
            if (item && typeof item === 'object') {
              const field = (item as any).field;
              const message = (item as any).message;
              if (field && typeof message === 'string') (serverErrors as any)[field] = message;
            }
          }
        }

        setCustomerFormErrors((prev) => ({ ...prev, ...serverErrors }));
        toast.error(serverErrors.form || 'Failed to create customer');
        return;
      }
      const savedCustomer: Customer = data.customer || data;

      if (isEditingCustomer) {
        setCustomers((prev) => prev.map((c) => c._id === savedCustomer._id ? savedCustomer : c));
        toast.success('Customer updated');
      } else {
        setCustomers((prev) => [savedCustomer, ...prev]);
        toast.success('Customer created');
      }

      setSelectedCustomer(savedCustomer);
      setShowCustomerModal(false);
      setIsEditingCustomer(false);
      setCustomerSearch('');
      setNewCustomer({ firstName: '', lastName: '', email: '', phone: '', postcode: '', city: '', address: '', gdprConsent: false, marketingConsent: false, preferredNotificationMethod: 'email' });
      setCustomerFormErrors({});
    } catch (e) {
      console.error(e);
      setCustomerFormErrors({ form: 'Failed to save customer' });
      toast.error('Failed to save customer');
    }
  };

  const handleCheckoutClick = async () => {
    if (!selectedCustomer) {
      toast.error('Select a customer');
      return;
    }
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    for (const item of cart) {
      if (item.service.pricingType === 'per_kg' && (!item.weight || item.weight <= 0)) {
        toast.error(`${item.service.name} requires a valid weight`);
        return;
      }
    }

    setIsLoading(true);
    try {
      const payload = {
        customerId: selectedCustomer._id,
        siteId: user?.siteId,
        orderType: 'in_store',
        dueDate,
        items: cart.map((i) => ({
          serviceId: i.serviceId,
          quantity: i.quantity,
          weight: i.weight,
          isExpress: i.isExpress,
          specialInstructions: i.specialInstructions,
        })),
      };

      const res = await fetch('/api/pos/orders', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Failed to create order');
        return;
      }

      const orderId = data?.order?._id || data?.order?.id || data?._id || data?.id;
      if (!orderId) {
        toast.error('Order created but missing order id');
        return;
      }

      setPendingOrderId(orderId);
      setShowPaymentModal(true);
    } catch (e) {
      console.error(e);
      toast.error('Failed to create order');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentCancel = async () => {
    if (pendingOrderId) {
      try {
        await fetch(`/api/pos/orders/${pendingOrderId}`, {
          method: 'DELETE',
          headers: getHeaders(),
        });
      } catch (e) {
        console.error('Failed to abandon order', e);
      }
    }
    setShowPaymentModal(false);
    setPendingOrderId(null);
  };

  const handlePaymentComplete = async (method: string) => {
    if (!pendingOrderId) return;

    setIsLoading(true);
    try {
      if (method === 'pay_on_collection') {
        setCompletedOrderId(pendingOrderId);
        setShowPaymentModal(false);
        setPendingOrderId(null);
        toast.success('Order placed (Pay on Collection)!');
        return;
      }

      const payRes = await fetch(`/api/pos/orders/${pendingOrderId}/payments`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ amount: total, method }),
      });

      const payData = await payRes.json();
      if (!payRes.ok) {
        toast.error(payData.error || 'Payment recording failed');
      }

      const completeRes = await fetch(`/api/pos/orders/${pendingOrderId}/complete`, {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!completeRes.ok) {
        const completeData = await completeRes.json().catch(() => null);
        toast.error(completeData?.error || 'Failed to finalize order');
        return;
      }

      setCompletedOrderId(pendingOrderId);
      setShowPaymentModal(false);
      setPendingOrderId(null);
      toast.success('Order completed successfully!');
    } catch (e) {
      console.error(e);
      toast.error('Failed to finalize order');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrderCompleteClose = () => {
    setCart([]);
    setSelectedCustomer(null);
    setCompletedOrderId(null);
  };

  const visibleCustomers = customers
    .filter((c) => {
      const q = customerSearch.trim().toLowerCase();
      if (!q) return true;
      return (
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q)
      );
    })
    .slice(0, 8);

  // ─── Cart Panel (shared between side panel and drawer) ───────────────────────
  const CartContents = ({ inDrawer = false }: { inDrawer?: boolean }) => (
    <>
      <div className="flex-1 overflow-y-auto p-4">
        {cart.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-base">Cart is empty</p>
            <p className="text-sm">Add services to begin</p>
          </div>
        ) : (
          <div className="space-y-4">
            {cart.map((item) => (
              <div key={item.serviceId} className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 pr-2">
                    <h4 className="font-semibold text-gray-900 text-base leading-tight">{item.service.name}</h4>
                    <p className="text-sm text-gray-500 mt-0.5">£{item.service.basePrice.toFixed(2)} each</p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.serviceId)}
                    className="w-9 h-9 flex items-center justify-center rounded-full text-red-400 hover:text-red-600 hover:bg-red-50 active:bg-red-100 flex-shrink-0 transition-colors"
                    aria-label="Remove item"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => updateQuantity(item.serviceId, item.quantity - 1)}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white border-2 border-gray-200 hover:border-primary-400 hover:bg-primary-50 active:bg-primary-100 transition-colors"
                      aria-label="Decrease quantity"
                    >
                      <Minus className="w-4 h-4 text-gray-700" />
                    </button>
                    <span className="w-10 text-center text-lg font-bold text-gray-900">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.serviceId, item.quantity + 1)}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white border-2 border-gray-200 hover:border-primary-400 hover:bg-primary-50 active:bg-primary-100 transition-colors"
                      aria-label="Increase quantity"
                    >
                      <Plus className="w-4 h-4 text-gray-700" />
                    </button>
                  </div>
                  <div className="text-lg font-bold text-primary-600">£{item.totalPrice.toFixed(2)}</div>
                </div>

                {item.service.pricingType === 'per_kg' && (
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Weight (kg)</label>
                    <input
                      type="number"
                      value={item.weight ?? ''}
                      onChange={(e) => updateWeight(item.serviceId, Number(e.target.value) || 0)}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg text-base focus:border-primary-400 focus:outline-none"
                      step="0.1"
                      min="0"
                    />
                  </div>
                )}

                {item.service.isExpress && (
                  <label className="flex items-center space-x-3 cursor-pointer py-1">
                    <div
                      onClick={() => toggleExpress(item.serviceId)}
                      className={`w-12 h-6 rounded-full flex-shrink-0 transition-colors relative cursor-pointer ${item.isExpress ? 'bg-primary-600' : 'bg-gray-200'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${item.isExpress ? 'translate-x-6' : 'translate-x-0'}`} />
                    </div>
                    <span className="text-sm text-gray-700 font-medium">Express (+£{item.service.expressSurcharge.toFixed(2)})</span>
                  </label>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Totals + Checkout */}
      <div className="bg-white border-t border-gray-200 p-4 space-y-3">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-base focus:border-primary-400 focus:outline-none"
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal</span>
            <span>£{subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm text-gray-600">
            <span>VAT (20%)</span>
            <span>£{vat.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-xl font-bold pt-1 border-t border-gray-100">
            <span>Total</span>
            <span className="text-primary-600">£{total.toFixed(2)}</span>
          </div>
        </div>

        {inDrawer && (
          <button
            onClick={() => setShowCartDrawer(false)}
            className="w-full py-3 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Continue Adding Items
          </button>
        )}

        <button
          onClick={() => { setShowCartDrawer(false); handleCheckoutClick(); }}
          disabled={cart.length === 0 || !selectedCustomer || isLoading}
          className="w-full py-4 text-lg font-bold rounded-xl bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
        >
          {isLoading ? 'Processing...' : `Checkout  £${total.toFixed(2)}`}
        </button>
        {!selectedCustomer && cart.length > 0 && (
          <p className="text-center text-xs text-amber-600">Select a customer to checkout</p>
        )}
      </div>
    </>
  );

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">

      {/* ── HEADER ───────────────────────────────────────────────────────── */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between h-16">
          {/* Left: title + site */}
          <div className="flex items-center gap-4 min-w-0">
            <img src="/logo.jpeg" alt="Logo" className="h-14 w-auto rounded" />
            {activeSiteName && (
              <div className="flex items-center text-sm font-medium text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full border border-primary-100">
                <MapPin className="w-3.5 h-3.5 mr-1" />
                {activeSiteName}
              </div>
            )}
            <div className="hidden md:flex items-center gap-1.5 text-xs text-gray-400">
              <Clock className="w-3.5 h-3.5" />
              <span>{new Date().toLocaleDateString()}</span>
            </div>
          </div>

          {/* Right: customer search + user menu + admin link */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Customer Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <input
                type="text"
                placeholder={selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : 'Customer...'}
                value={customerSearch}
                onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                onFocus={() => setShowCustomerDropdown(true)}
                className="pl-9 pr-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none text-sm w-40 sm:w-52 md:w-64 transition-all"
              />
              {selectedCustomer && !customerSearch && (
                <button
                  onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {showCustomerDropdown && (
                <div className="absolute left-0 mt-1.5 w-80 sm:w-96 bg-white shadow-xl rounded-xl border border-gray-200 z-50 overflow-hidden">
                  <div className="max-h-72 overflow-auto divide-y divide-gray-50">
                    {visibleCustomers.length === 0 ? (
                      <div className="p-4 text-sm text-gray-500 text-center">No customers found</div>
                    ) : (
                      visibleCustomers.map((c) => (
                        <button
                          key={c._id}
                          onClick={() => { setSelectedCustomer(c); setShowCustomerDropdown(false); setCustomerSearch(''); }}
                          className="w-full text-left px-4 py-3.5 hover:bg-primary-50 active:bg-primary-100 transition-colors"
                        >
                          <div className="text-sm font-semibold text-gray-900">{c.firstName} {c.lastName}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{c.email}{c.phone ? ` • ${c.phone}` : ''}</div>
                        </button>
                      ))
                    )}
                  </div>
                  <div className="p-3 bg-gray-50 border-t border-gray-100 flex gap-2">
                    <button
                      onClick={() => {
                        setIsEditingCustomer(false);
                        setNewCustomer({ firstName: '', lastName: '', email: '', phone: '', postcode: '', city: '', address: '', gdprConsent: false, marketingConsent: false, preferredNotificationMethod: 'email' });
                        setShowCustomerDropdown(false);
                        setShowCustomerModal(true);
                      }}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2.5 text-sm font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 active:bg-primary-200 rounded-lg transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-1.5" />
                      Add New Customer
                    </button>
                    <button
                      onClick={() => setShowCustomerDropdown(false)}
                      className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu((v) => !v)}
                className="flex items-center gap-2 px-3 py-2.5 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300 transition-colors"
              >
                <User className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700 hidden sm:inline">
                  {user ? `${user.firstName}` : 'User'}
                </span>
              </button>
              {showUserMenu && (
                <div className="absolute right-0 mt-1.5 w-48 bg-white rounded-xl shadow-lg py-1 z-50 border border-gray-100">
                  <button
                    onClick={logout}
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 w-full"
                  >
                    <LogOut className="w-4 h-4 mr-2 text-gray-500" />
                    Logout
                  </button>
                </div>
              )}
            </div>

            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <Link
                to="/admin"
                className="hidden sm:flex items-center gap-1.5 px-3 py-2.5 bg-primary-50 text-primary-700 rounded-xl hover:bg-primary-100 active:bg-primary-200 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span className="text-sm font-medium">Admin</span>
              </Link>
            )}
          </div>
        </div>
      </header >

      {/* ── MAIN CONTENT ─────────────────────────────────────────────────── */}
      < div className="flex-1 flex overflow-hidden" >

        {/* ── LEFT: services panel ─────────────────────────────────────── */}
        < div className="flex-1 flex flex-col overflow-hidden" >

          {/* Category tabs */}
          < div className="bg-white border-b border-gray-200 px-3 py-2 flex-shrink-0" >
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap ${selectedCategory === category.id
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 bg-gray-100 hover:bg-gray-200 active:bg-gray-300'
                    }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div >

          {/* Service search */}
          < div className="px-4 py-3 bg-white border-b border-gray-100 flex-shrink-0" >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <input
                type="text"
                placeholder="Search services..."
                value={serviceSearch}
                onChange={(e) => setServiceSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none text-sm"
              />
            </div>
          </div >

          {/* Services grid */}
          < div className="flex-1 overflow-y-auto p-4" >
            {
              filteredServices.length === 0 ? (
                <div className="text-center text-gray-400 mt-16">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p className="text-base">No services found</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {filteredServices.map((service) => (
                    <button
                      key={service._id}
                      onClick={() => addToCart(service)}
                      className="bg-white p-4 rounded-xl shadow-sm border-2 border-gray-100 hover:border-primary-300 hover:shadow-md active:border-primary-500 active:bg-primary-50 transition-all text-left min-h-[90px] flex flex-col justify-between"
                    >
                      <div>
                        <h3 className="font-bold text-gray-900 text-base leading-tight">{service.name}</h3>
                        <p className="text-xs text-gray-400 mt-0.5 capitalize">{service.category.replace('_', ' ')}</p>
                      </div>
                      <div className="mt-2">
                        <p className="text-lg font-bold text-primary-600">£{service.basePrice.toFixed(2)}</p>
                        {service.isExpress && (
                          <p className="text-xs text-orange-500 font-medium">Express +£{service.expressSurcharge.toFixed(2)}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )
            }
          </div >
        </div >

        {/* ── RIGHT: Cart side panel (hidden on narrow tablets) ────────── */}
        < div className="hidden lg:flex w-80 xl:w-96 bg-white border-l border-gray-200 flex-col flex-shrink-0" >
          <div className="px-4 py-3.5 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary-600" />
                Cart
                {cart.length > 0 && (
                  <span className="bg-primary-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{cart.length}</span>
                )}
              </h2>
              {selectedCustomer && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-sm text-primary-700 bg-primary-50 px-2.5 py-1 rounded-lg">
                    <User className="w-3.5 h-3.5" />
                    <span className="font-medium">{selectedCustomer.firstName}</span>
                  </div>
                  <button
                    onClick={() => {
                      setNewCustomer({
                        firstName: selectedCustomer.firstName || '',
                        lastName: selectedCustomer.lastName || '',
                        email: selectedCustomer.email || '',
                        phone: selectedCustomer.phone || '',
                        postcode: selectedCustomer.postcode || '',
                        city: selectedCustomer.city || '',
                        address: selectedCustomer.address || '',
                        gdprConsent: selectedCustomer.gdprConsent || false,
                        marketingConsent: selectedCustomer.marketingConsent || false,
                        preferredNotificationMethod: selectedCustomer.preferredNotificationMethod || 'email',
                      });
                      setIsEditingCustomer(true);
                      setShowCustomerModal(true);
                    }}
                    className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Edit Customer"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
          <CartContents />
        </div >
      </div >

      {/* ── BOTTOM CART FAB (tablets < lg) ──────────────────────────────── */}
      < div className="lg:hidden fixed bottom-0 inset-x-0 z-40" >
        {/* Backdrop */}
        {
          showCartDrawer && (
            <div
              className="fixed inset-0 bg-black/40 z-40"
              onClick={() => setShowCartDrawer(false)}
            />
          )
        }

        {/* Slide-up cart drawer */}
        <div className={`absolute bottom-0 inset-x-0 bg-white rounded-t-2xl shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-out ${showCartDrawer ? 'translate-y-0' : 'translate-y-full'}`}
          style={{ maxHeight: '85vh' }}>
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-primary-600" />
              Cart ({cart.length})
            </h2>
            <div className="flex items-center gap-3">
              {selectedCustomer && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-sm text-primary-700 bg-primary-50 px-2.5 py-1 rounded-lg">
                    <User className="w-3.5 h-3.5" />
                    <span className="font-medium">{selectedCustomer.firstName}</span>
                  </div>
                  <button
                    onClick={() => {
                      setNewCustomer({
                        firstName: selectedCustomer.firstName || '',
                        lastName: selectedCustomer.lastName || '',
                        email: selectedCustomer.email || '',
                        phone: selectedCustomer.phone || '',
                        postcode: selectedCustomer.postcode || '',
                        city: selectedCustomer.city || '',
                        address: selectedCustomer.address || '',
                        gdprConsent: selectedCustomer.gdprConsent || false,
                        marketingConsent: selectedCustomer.marketingConsent || false,
                        preferredNotificationMethod: selectedCustomer.preferredNotificationMethod || 'email',
                      });
                      setIsEditingCustomer(true);
                      setShowCartDrawer(false);
                      setShowCustomerModal(true);
                    }}
                    className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Edit Customer"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              )}
              <button
                onClick={() => setShowCartDrawer(false)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto flex flex-col">
            <CartContents inDrawer={true} />
          </div>
        </div>

        {/* Sticky bottom bar (always visible) */}
        {
          !showCartDrawer && (
            <button
              onClick={() => setShowCartDrawer(true)}
              className="w-full bg-primary-600 text-white flex items-center justify-between px-5 py-4 hover:bg-primary-700 active:bg-primary-800 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <ShoppingCart className="w-6 h-6" />
                  {cart.length > 0 && (
                    <span className="absolute -top-2 -right-2 bg-white text-primary-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow">
                      {cart.length}
                    </span>
                  )}
                </div>
                <span className="font-semibold text-base">
                  {cart.length === 0 ? 'Cart is empty' : `${cart.length} item${cart.length !== 1 ? 's' : ''}`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold">£{total.toFixed(2)}</span>
                <ChevronUp className="w-5 h-5 opacity-80" />
              </div>
            </button>
          )
        }
      </div >

      {/* ── MODALS ───────────────────────────────────────────────────────── */}
      {
        showPaymentModal && pendingOrderId && (
          <PaymentModal
            orderId={pendingOrderId}
            amount={total}
            onClose={handlePaymentCancel}
            onComplete={handlePaymentComplete}
          />
        )
      }

      {
        completedOrderId && (
          <OrderCompleteModal
            orderId={completedOrderId}
            onClose={handleOrderCompleteClose}
          />
        )
      }

      {/* Add / Edit Customer Modal */}
      {
        showCustomerModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white w-full sm:rounded-2xl sm:max-w-lg overflow-hidden flex flex-col max-h-screen sm:max-h-[90vh]">
              {/* Modal header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
                <h3 className="text-lg font-bold text-gray-900">{isEditingCustomer ? 'Edit Customer' : 'Add New Customer'}</h3>
                <button
                  onClick={() => { setShowCustomerModal(false); setNewCustomer({ firstName: '', lastName: '', email: '', phone: '', postcode: '', city: '', address: '', gdprConsent: false, marketingConsent: false, preferredNotificationMethod: 'email' }); setCustomerFormErrors({}); }}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Modal body */}
              <div className="overflow-y-auto flex-1 p-5">
                {customerFormErrors.form && (
                  <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {customerFormErrors.form}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  {/* First name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First name <span className="text-red-500">*</span></label>
                    <input
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 ${customerFormErrors.firstName ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      value={newCustomer.firstName}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, firstName: e.target.value })); if (customerFormErrors.firstName) setCustomerFormErrors((p) => ({ ...p, firstName: undefined })); }}
                    />
                    {customerFormErrors.firstName && <div className="mt-1 text-xs text-red-600">{customerFormErrors.firstName}</div>}
                  </div>
                  {/* Last name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last name <span className="text-red-500">*</span></label>
                    <input
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 ${customerFormErrors.lastName ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      value={newCustomer.lastName}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, lastName: e.target.value })); if (customerFormErrors.lastName) setCustomerFormErrors((p) => ({ ...p, lastName: undefined })); }}
                    />
                    {customerFormErrors.lastName && <div className="mt-1 text-xs text-red-600">{customerFormErrors.lastName}</div>}
                  </div>
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 ${customerFormErrors.email ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      value={newCustomer.email}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, email: e.target.value })); if (customerFormErrors.email) setCustomerFormErrors((p) => ({ ...p, email: undefined })); }}
                    />
                    {customerFormErrors.email && <div className="mt-1 text-xs text-red-600">{customerFormErrors.email}</div>}
                  </div>
                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 ${customerFormErrors.phone ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      value={newCustomer.phone}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, phone: e.target.value })); if (customerFormErrors.phone) setCustomerFormErrors((p) => ({ ...p, phone: undefined })); }}
                    />
                    {customerFormErrors.phone && <div className="mt-1 text-xs text-red-600">{customerFormErrors.phone}</div>}
                  </div>
                  {/* Postcode */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                    <input
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 ${customerFormErrors.postcode ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      value={newCustomer.postcode}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, postcode: e.target.value })); if (customerFormErrors.postcode) setCustomerFormErrors((p) => ({ ...p, postcode: undefined })); }}
                    />
                    {customerFormErrors.postcode && <div className="mt-1 text-xs text-red-600">{customerFormErrors.postcode}</div>}
                  </div>
                  {/* City */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 ${customerFormErrors.city ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      value={newCustomer.city}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, city: e.target.value })); if (customerFormErrors.city) setCustomerFormErrors((p) => ({ ...p, city: undefined })); }}
                    />
                    {customerFormErrors.city && <div className="mt-1 text-xs text-red-600">{customerFormErrors.city}</div>}
                  </div>
                  {/* Address */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <textarea
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 resize-none ${customerFormErrors.address ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      rows={2}
                      value={newCustomer.address}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, address: e.target.value })); if (customerFormErrors.address) setCustomerFormErrors((p) => ({ ...p, address: undefined })); }}
                    />
                    {customerFormErrors.address && <div className="mt-1 text-xs text-red-600">{customerFormErrors.address}</div>}
                  </div>

                  {/* Preferences & Consents */}
                  <div className="col-span-2 grid grid-cols-1 gap-4 pt-4 border-t border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferences & Consents</label>
                      <div className="space-y-3">
                        <label className="flex items-center text-sm font-medium text-gray-700">
                          <input
                            type="checkbox"
                            className="mr-2 rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-offset-2 focus:ring-primary-500"
                            checked={newCustomer.gdprConsent}
                            onChange={(e) => setNewCustomer((p) => ({ ...p, gdprConsent: e.target.checked }))}
                          />
                          GDPR Consent
                        </label>
                        <label className="flex items-center text-sm font-medium text-gray-700">
                          <input
                            type="checkbox"
                            className="mr-2 rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-offset-2 focus:ring-primary-500"
                            checked={newCustomer.marketingConsent}
                            onChange={(e) => setNewCustomer((p) => ({ ...p, marketingConsent: e.target.checked }))}
                          />
                          Marketing Consent
                        </label>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Notification Method</label>
                          <select
                            className="w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 border-gray-200"
                            value={newCustomer.preferredNotificationMethod}
                            onChange={(e) => setNewCustomer((p) => ({ ...p, preferredNotificationMethod: e.target.value as any }))}
                          >
                            <option value="email">Email</option>
                            <option value="sms">SMS</option>
                            <option value="both">Both</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal footer */}
              <div className="flex gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
                <button
                  className="flex-1 py-3.5 text-base font-semibold rounded-xl border-2 border-gray-200 text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                  onClick={() => { setShowCustomerModal(false); setNewCustomer({ firstName: '', lastName: '', email: '', phone: '', postcode: '', city: '', address: '', gdprConsent: false, marketingConsent: false, preferredNotificationMethod: 'email' }); setCustomerFormErrors({}); }}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-3.5 text-base font-bold rounded-xl bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 transition-colors shadow-md"
                  onClick={handleSaveCustomer}
                >
                  {isEditingCustomer ? 'Save Changes' : 'Create Customer'}
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default POS;
